import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Zap, Play, Pause, Trash2, ChevronRight, Loader, TrendingUp, Activity, Clock, Search, AlertTriangle, CheckCircle, Sparkles } from 'lucide-react';
import api from '../api';

const STATUS = {
  active:   { label: 'Active',   cls: 'badge-active',   color: 'var(--green)' },
  paused:   { label: 'Paused',   cls: 'badge-paused',   color: 'var(--yellow)' },
  building: { label: 'Building', cls: 'badge-building', color: 'var(--accent3)' },
  ready:    { label: 'Ready',    cls: 'badge-ready',    color: 'var(--blue)' },
  failed:   { label: 'Failed',   cls: 'badge-failed',   color: 'var(--red)' },
  stopped:  { label: 'Stopped',  cls: 'badge-stopped',  color: 'var(--text3)' },
};

const QUICK_IDEAS = [
  { title: 'Lead Capture', desc: 'Form → Sheets + Email', icon: '📥' },
  { title: 'Weekly Digest', desc: 'Calendar → Telegram', icon: '📅' },
  { title: 'Invoice Handler', desc: 'Email → Drive + Notify', icon: '🧾' },
  { title: 'CRM Auto-Update', desc: 'New contact → CRM', icon: '👤' },
  { title: 'Sales Alert', desc: 'Stripe payment → Slack', icon: '💳' },
  { title: 'Content Scheduler', desc: 'Sheet → Social post', icon: '📢' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [user, setUser] = useState(null);
  const [search, setSearch] = useState('');
  const [tierLimit, setTierLimit] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
  }, []);

  const load = async () => {
    try {
      const [aRes, tRes] = await Promise.all([api.get('/automations'), api.get('/tiers')]);
      setAutomations(aRes.data);
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      setTierLimit(tRes.data.find(t => t.tier === u.tier));
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const toggle = async (id, e) => {
    e.stopPropagation(); setToggling(id);
    try { await api.post(`/automations/${id}/toggle`); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setToggling(null); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this automation permanently?')) return;
    try { await api.delete(`/automations/${id}`); await load(); }
    catch { alert('Failed'); }
  };

  const filtered = automations.filter(a =>
    search === '' || a.title.toLowerCase().includes(search.toLowerCase()) || a.description.toLowerCase().includes(search.toLowerCase())
  );

  const active = automations.filter(a => a.status === 'active').length;
  const building = automations.filter(a => a.status === 'building').length;
  const failed = automations.filter(a => a.status === 'failed').length;
  const activeCount = automations.filter(a => a.status !== 'stopped').length;
  const usagePercent = tierLimit && tierLimit.max_automations < 999
    ? Math.min(100, Math.round((activeCount / tierLimit.max_automations) * 100)) : null;
  const timeOfDay = new Date().getHours() < 12 ? 'MORNING' : new Date().getHours() < 17 ? 'AFTERNOON' : 'EVENING';

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <Loader size={28} color="var(--accent)" style={{ animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 44, marginBottom: 4 }}>GOOD {timeOfDay}{user ? `, ${user.full_name?.split(' ')[0].toUpperCase()}` : ''} 👋</h1>
          <p style={{ color: 'var(--text2)', fontSize: 15, fontWeight: 500 }}>
            {automations.length === 0 ? 'Ready to build your first automation?' : `${active} running · ${automations.length} total · ${building > 0 ? `${building} building` : 'all stable'}`}
          </p>
        </div>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('/new')}>
          <Plus size={17} /> New Automation
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'TOTAL', value: automations.length, icon: <Activity size={18} />, color: 'var(--text)', border: 'var(--border)' },
          { label: 'ACTIVE', value: active, icon: <TrendingUp size={18} />, color: 'var(--green)', border: 'rgba(34,197,94,0.2)' },
          { label: 'BUILDING', value: building, icon: <Clock size={18} />, color: 'var(--accent3)', border: 'rgba(99,102,241,0.2)' },
          { label: 'FAILED', value: failed, icon: <AlertTriangle size={18} />, color: failed > 0 ? 'var(--red)' : 'var(--text3)', border: failed > 0 ? 'rgba(239,68,68,0.2)' : 'var(--border)' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg2)', border: `1px solid ${s.border}`, borderRadius: 'var(--radius)', padding: '20px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 52, color: s.color, lineHeight: 0.9, letterSpacing: '0.02em' }}>{s.value}</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 8 }}>{s.label}</div>
            </div>
            <div style={{ color: s.color, opacity: 0.5, background: `${s.color}15`, padding: 10, borderRadius: 10, border: `1px solid ${s.border}` }}>{s.icon}</div>
          </div>
        ))}
      </div>

      {/* Usage + Alert row */}
      <div style={{ display: 'grid', gridTemplateColumns: usagePercent !== null ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 20 }}>
        {usagePercent !== null && (
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '18px 22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Plan Usage</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: usagePercent >= 80 ? 'var(--red)' : 'var(--text2)' }}>{activeCount} / {tierLimit.max_automations}</span>
            </div>
            <div style={{ height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${usagePercent}%`, background: usagePercent >= 80 ? 'linear-gradient(90deg, var(--red), #ff6b6b)' : 'linear-gradient(90deg, var(--accent), var(--purple))', borderRadius: 3, transition: 'width 0.5s ease' }} />
            </div>
            {usagePercent >= 80
              ? <p style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600 }}>⚠️ Running low — <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/billing')}>upgrade plan</span></p>
              : <p style={{ fontSize: 12, color: 'var(--text3)' }}>{tierLimit.max_automations - activeCount} slots remaining</p>
            }
          </div>
        )}
        {/* AI tip card */}
        <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(168,85,247,0.08))', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 'var(--radius)', padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-glow)', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Sparkles size={18} color="var(--accent3)" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>Pro Tip</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>The more specific your description, the better the AI builds your automation. Include platform names and triggers.</div>
          </div>
        </div>
      </div>

      {/* Two column layout when has automations */}
      <div style={{ display: 'grid', gridTemplateColumns: automations.length > 0 ? '1fr 280px' : '1fr', gap: 16 }}>
        {/* Left: automations list */}
        <div>
          {automations.length > 0 && (
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={14} color="var(--text3)" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search automations..." style={{ paddingLeft: 38, fontSize: 13 }} />
            </div>
          )}

          {automations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 32px', background: 'var(--bg2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <div style={{ width: 72, height: 72, background: 'var(--accent-glow)', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', border: '1px solid rgba(99,102,241,0.25)', animation: 'glow-pulse 3s ease infinite' }}>
                <Zap size={32} color="var(--accent)" fill="var(--accent)" />
              </div>
              <div className="display" style={{ fontSize: 36, marginBottom: 10 }}>BUILD YOUR FIRST AUTOMATION</div>
              <p style={{ color: 'var(--text2)', fontSize: 14, maxWidth: 360, margin: '0 auto 28px', lineHeight: 1.8 }}>
                Describe what you want to automate in plain English. Our AI builds and deploys it in seconds.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/new')}>
                <Plus size={17} /> Create Automation
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Your Automations ({filtered.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.map((a, idx) => {
                  const s = STATUS[a.status] || STATUS.stopped;
                  return (
                    <div key={a.id}
                      style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)' }}
                      onClick={() => navigate(`/automation/${a.id}`)}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.transform = 'translateX(2px)'; e.currentTarget.style.background = 'var(--bg3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.background = 'var(--bg2)'; }}>
                      <div style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0, background: a.status === 'active' ? 'var(--green-bg)' : 'var(--bg3)', border: `1px solid ${a.status === 'active' ? 'var(--green-border)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: a.status === 'active' ? '0 0 12px rgba(34,197,94,0.25)' : 'none' }}>
                        <Zap size={17} color={a.status === 'active' ? 'var(--green)' : 'var(--text3)'} fill={a.status === 'active' ? 'var(--green)' : 'none'} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontWeight: 700, fontSize: 14 }}>{a.title}</span>
                          <span className={`badge ${s.cls}`}>{a.status === 'building' && <Loader size={9} style={{ animation: 'spin 1s linear infinite' }} />}{s.label}</span>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description}</p>
                        {a.required_integrations?.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                            {a.required_integrations.slice(0, 4).map(i => (
                              <span key={i} style={{ fontSize: 10, padding: '1px 7px', background: 'var(--bg4)', borderRadius: 20, color: 'var(--text3)', border: '1px solid var(--border2)', fontWeight: 700 }}>{i}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        {a.status === 'building'
                          ? <Loader size={15} color="var(--accent3)" style={{ animation: 'spin 1s linear infinite', margin: 8 }} />
                          : a.status !== 'failed' && (
                            <button className="btn btn-secondary btn-icon" style={{ width: 32, height: 32 }} onClick={e => toggle(a.id, e)} disabled={toggling === a.id}>
                              {toggling === a.id ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : a.status === 'active' ? <Pause size={13} /> : <Play size={13} />}
                            </button>
                          )
                        }
                        <button className="btn btn-danger btn-icon" style={{ width: 32, height: 32 }} onClick={e => remove(a.id, e)}><Trash2 size={13} /></button>
                      </div>
                      <ChevronRight size={14} color="var(--text3)" style={{ flexShrink: 0 }} />
                    </div>
                  );
                })}
                {filtered.length === 0 && search && <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 14 }}>No results for "{search}"</div>}
              </div>
            </div>
          )}
        </div>

        {/* Right: Quick ideas sidebar */}
        {automations.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Quick Ideas</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {QUICK_IDEAS.map(idea => (
                <div key={idea.title}
                  onClick={() => navigate('/new')}
                  style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'; e.currentTarget.style.background = 'var(--bg3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg2)'; }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{idea.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 1 }}>{idea.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{idea.desc}</div>
                  </div>
                  <Plus size={13} color="var(--text3)" style={{ marginLeft: 'auto', flexShrink: 0 }} />
                </div>
              ))}
            </div>

            {/* Status legend */}
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Status Guide</div>
              {[
                { color: 'var(--blue)', label: 'Ready', desc: 'Built, needs activation' },
                { color: 'var(--green)', label: 'Active', desc: 'Running live' },
                { color: 'var(--yellow)', label: 'Paused', desc: 'Temporarily off' },
                { color: 'var(--accent3)', label: 'Building', desc: 'AI is working...' },
                { color: 'var(--red)', label: 'Failed', desc: 'Needs attention' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', minWidth: 60 }}>{s.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{s.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
